'use es6';

import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import I18n from 'I18n';
import PropTypes from 'prop-types';
import { Children, memo } from 'react';
import devLogger from 'react-utils/devLogger';
import UIStepIndicator from '../connectedStep/UIStepIndicator';
import { usePanelContext } from '../context/PanelContext';
import H4 from '../elements/headings/H4';
import UIFlex from '../layout/UIFlex';
import UIPanelSection from '../panel/UIPanelSection';
import UIDialogCloseButton from './UIDialogCloseButton';
import UIDialogHeader from './UIDialogHeader';

const getStepNames = (wizardProps, inPanel) => {
  const steps = Children.toArray(wizardProps.children);
  const stepNames = [];
  const stepLimit = inPanel ? 4 : 6;
  let prevStepName;
  steps.forEach(step => {
    const stepName = step.props.name;

    if (process.env.NODE_ENV !== 'production') {
      if (stepName != null && !(typeof stepName === 'string' && stepName.length > 0)) {
        devLogger.warn({
          message: 'UIWizardHeaderWithOverview: a UIWizardStep is missing a `name` attribute. Expected `name` to be an I18n.text (string).',
          key: 'UIWizardHeaderWithOverview: missing wizard step name'
        });
      }
    }

    if (stepName && stepName !== prevStepName) {
      stepNames.push(stepName);
      prevStepName = stepName;
    }
  });

  if (stepNames.length > stepLimit) {
    if (process.env.NODE_ENV !== 'production') {
      devLogger.warn({
        message: `UIWizardHeaderWithOverview: ${stepNames.length} steps given, but the maximum is ${stepLimit}.`,
        key: 'UIWizardHeaderWithOverview: Too many steps'
      });
    }
  }

  return stepNames;
};

const renderStepIndicator = (stepNames, currentStepName, inPanel) => currentStepName ? /*#__PURE__*/_jsx(UIStepIndicator, {
  direction: "horizontal",
  stepIndex: stepNames.indexOf(currentStepName),
  stepNames: stepNames,
  use: inPanel ? 'flush' : 'default'
}) : '';

const renderStepCount = (stepNames, currentStepName) => {
  if (!currentStepName) {
    return null;
  }

  const stepNumber = stepNames.indexOf(currentStepName) + 1;
  const stepCount = stepNames.length;
  return /*#__PURE__*/_jsx("div", {
    className: "private-wizard__header-step-count",
    children: I18n.text('salesUI.UIWizard.stepCount', {
      stepNumber,
      stepCount
    })
  });
}; // Ticket to add missing prop types: https://git.hubteam.com/HubSpot/UIComponents/issues/11738


const UIWizardHeaderWithOverview = /*#__PURE__*/memo(props => {
  const {
    stepProps,
    wizardProps,
    // eslint-disable-next-line react/prop-types
    headerdatatestid
  } = props;
  const {
    inPanel
  } = usePanelContext();
  const stepHeading = stepProps.heading;
  const defaultHeading = wizardProps.defaultHeading;
  const stepNames = getStepNames(wizardProps, inPanel);
  const classes = 'uiWizardHeader' + (!inPanel ? " private-wizard__header-with-overview" : "");
  const currentStepName = stepProps && stepProps.name;
  return /*#__PURE__*/_jsxs("div", {
    children: [inPanel && /*#__PURE__*/_jsx(UIDialogCloseButton, {
      onClick: wizardProps.onReject,
      "data-wizard-action-name": "close"
    }), /*#__PURE__*/_jsxs(UIDialogHeader, {
      className: classes,
      "data-test-id": headerdatatestid,
      children: [inPanel ? /*#__PURE__*/_jsx(H4, {
        children: wizardProps.defaultHeading
      }) : /*#__PURE__*/_jsx("span", {
        "aria-level": 2,
        className: "private-wizard__title",
        role: "heading",
        children: stepHeading || defaultHeading
      }), !inPanel && renderStepIndicator(stepNames, currentStepName, inPanel), currentStepName && !inPanel && renderStepCount(stepNames, currentStepName)]
    }), inPanel && /*#__PURE__*/_jsx(UIPanelSection, {
      className: "private-panel__steps",
      children: /*#__PURE__*/_jsx(UIFlex, {
        children: renderStepIndicator(stepNames, currentStepName, inPanel)
      })
    })]
  });
});
UIWizardHeaderWithOverview.propTypes = {
  stepProps: PropTypes.object.isRequired,
  wizardProps: PropTypes.object.isRequired
};
UIWizardHeaderWithOverview.displayName = 'UIWizardHeaderWithOverview';
export default UIWizardHeaderWithOverview;